import type { ApiErrorBody } from "../api/types";
import { SuiteApiError } from "@weco/suite-client";

const FRIENDLY: Record<string, string> = {
  cross_org_forbidden: "That user isn't in your organization.",
  not_a_member: "You're not a member of this conversation.",
  // Group DMs became mutable in #1597 Pass 2; this code is now raised only for
  // a 1:1 dm (viewsets.py:479,561), so naming group DMs here contradicts the
  // add/remove controls the user can see in one.
  membership_locked: "Membership of a one-to-one conversation is fixed.",
  thread_too_deep: "You can only reply to a top-level message.",
  edit_window_expired: "You can only edit a message within 15 minutes of sending.",
  all_mention_not_allowed: "You can't @everyone in this space.",
  // Still raised by DELETE /members/{sub}/, which has no nomination channel
  // (#1619 Pass 6) — the copy points at the door that does.
  last_owner_cannot_leave: "Promote another owner before you leave.",
  // Owner succession (#1619 AC43/AC46). Both are backstops: the header only
  // opens the leave request once a nominee has been picked from the space's
  // own member list, so a user should never read either of these.
  successor_required: "Choose who becomes owner before you leave.",
  invalid_successor: "That person is no longer an active member of this space.",
  // The frozen pair (#1619). `conversation_frozen` guards every write on a
  // conversation the caller has left; `conversation_not_frozen` is its mirror
  // and guards "Remove from list", which only a former member may use.
  conversation_frozen: "You're no longer a member of this conversation.",
  conversation_not_frozen: "You can only remove a conversation you've left.",
  quote_cross_conversation_forbidden: "You can only quote messages from the same conversation.",
  parent_cross_conversation_forbidden: "Replies must stay in the parent conversation.",
  custom_emoji_name_taken: "An emoji with that name already exists.",
  custom_emoji_org_limit: "Your organization has reached its custom emoji limit.",
  not_joinable: "This space isn't open for self-join.",
  archived: "This space is archived.",
  file_too_large: "That file is too large (max 50 MB).",
  mime_not_allowed: "That file type isn't allowed.",
  rate_limited: "You're sending messages too fast — slow down a bit.",
  unauthorized: "Your session has expired. Please reload.",
  forbidden: "You don't have permission to do that.",
  not_found: "We couldn't find that.",
  conflict: "That action conflicts with the current state.",
  validation_failed: "Some fields need to be fixed.",
  bad_request: "We couldn't process that request.",
};

const FALLBACK = "Something went wrong. Try again.";

export function errorBodyOf(err: unknown): ApiErrorBody | null {
  if (err instanceof SuiteApiError && err.details && typeof err.details === "object") {
    return err.details as ApiErrorBody;
  }
  return null;
}

export function friendlyError(err: unknown): string {
  const body = errorBodyOf(err);
  if (!body) return err instanceof Error ? err.message || FALLBACK : FALLBACK;
  return FRIENDLY[body.error_code] ?? body.detail ?? FALLBACK;
}

export function fieldErrorsOf(err: unknown): Record<string, string[]> | null {
  const body = errorBodyOf(err);
  return body?.field_errors ?? null;
}
